import React, { useEffect, useCallback, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { WHEEL_ITEM_RADIUS, WHEEL_ITEM_SIZE } from './constants';
import { setContainerStyles, setSlideStyles } from './utils';

const IOSPickerWheel = ({
  values,
  selectedValue,
  onValueChange,
  unit = '',
  disabled = false
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    axis: 'y',
    dragFree: true,
    containScroll: false,
    watchSlides: false
  });

  const rootNodeRef = useRef(null);
  const slideCount = values.length;
  const totalRadius = slideCount * WHEEL_ITEM_RADIUS;
  const rotationOffset = WHEEL_ITEM_RADIUS; // pas de loop donc on garde un offset
  const slides = Array.from(Array(slideCount).keys());

  const inactivateEmblaTransform = useCallback((emblaApi) => {
    if (!emblaApi) return;
    const { translate, slideLooper } = emblaApi.internalEngine();
    translate.clear();
    translate.toggleActive(false);
    slideLooper.loopPoints.forEach(({ translate }) => {
      translate.clear();
      translate.toggleActive(false);
    });
  }, []);

  const rotateWheel = useCallback(
    (emblaApi) => {
      const rotation = slideCount * WHEEL_ITEM_RADIUS - rotationOffset;
      const wheelRotation = rotation * emblaApi.scrollProgress();
      setContainerStyles(emblaApi, wheelRotation);
      emblaApi.slideNodes().forEach((_, index) => {
        setSlideStyles(emblaApi, index, false, slideCount, totalRadius);
      });
    },
    [slideCount, rotationOffset, totalRadius]
  );

  const scrollToValue = useCallback((value) => {
    if (!emblaApi) return;
    const index = values.indexOf(value);
    if (index !== -1) {
      emblaApi.scrollTo(index);
    }
  }, [emblaApi, values]);

  useEffect(() => {
    if (!emblaApi) return;

    let wasInterrupted = false;

    // Arrêter toute vélocité en cours sur click/touch
    emblaApi.on('pointerDown', (emblaApi) => {
      const { scrollTo, target, location } = emblaApi.internalEngine();
      const currentLocation = location.get();

      // Marquer comme interrompu pour éviter l'inertie sur pointerUp
      wasInterrupted = true;

      // Arrêter immédiatement tout mouvement et synchroniser target avec location
      scrollTo.distance(0, false);
      target.set(currentLocation);
    });

    // Logique de snap avec inertie contrôlée
    emblaApi.on('pointerUp', (emblaApi) => {
      const { scrollTo, target, location } = emblaApi.internalEngine();
      const diffToTarget = target.get() - location.get();
      const absDiff = Math.abs(diffToTarget);

      // Si le mouvement a été interrompu, pas d'inertie automatique
      if (wasInterrupted) {
        wasInterrupted = false;
        // Snap vers l'élément le plus proche sans inertie
        const currentLocation = location.get();
        const snapIndex = emblaApi.selectedScrollSnap();
        emblaApi.scrollTo(snapIndex);
        return;
      }

      // Calculer la vélocité approximative basée sur la distance
      const velocity = absDiff / (WHEEL_ITEM_SIZE * 5);

      // Si mouvement très lent ou très proche, snap précis
      if (absDiff < WHEEL_ITEM_SIZE / 3 || velocity < 0.5) {
        scrollTo.distance(diffToTarget, true);
        // Déclencher immédiatement le changement pour les petits mouvements
        setTimeout(() => {
          const selectedIndex = emblaApi.selectedScrollSnap();
          const newValue = values[selectedIndex];
          if (newValue !== selectedValue) {
            onValueChange(newValue);
          }
        }, 50);
      }
      // Si mouvement modéré, permettre un peu d'inertie mais contrôlée
      else if (absDiff < WHEEL_ITEM_SIZE * 2) {
        // Facteur d'inertie progressif basé sur la vélocité
        const inertieFactor = Math.min(0.3, Math.max(0.1, velocity * 0.002));
        const distance = diffToTarget * inertieFactor;
        scrollTo.distance(distance, true);
      }
      // Si mouvement rapide, limiter l'inertie pour éviter les sauts excessifs
      else {
        // Inertie limitée pour les grands mouvements
        const maxInertie = WHEEL_ITEM_SIZE * 1; // Limite à 1 élément max
        const limitedDistance = Math.sign(diffToTarget) * Math.min(absDiff * 1.2, maxInertie);
        scrollTo.distance(limitedDistance, true);
      }
    });

    emblaApi.on('scroll', rotateWheel);

    // Déclencher le changement dès que le scroll s'arrête avec un debounce
    let scrollTimeout;
    emblaApi.on('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const selectedIndex = emblaApi.selectedScrollSnap();
        const newValue = values[selectedIndex];
        if (newValue !== selectedValue) {
          onValueChange(newValue);
        }
      }, 100); // Délai réduit pour une réponse plus rapide
    });

    emblaApi.on('settle', () => {
      const selectedIndex = emblaApi.selectedScrollSnap();
      const newValue = values[selectedIndex];
      if (newValue !== selectedValue) {
        onValueChange(newValue);
      }
    });

    emblaApi.on('reInit', (emblaApi) => {
      inactivateEmblaTransform(emblaApi);
      rotateWheel(emblaApi);
    });

    inactivateEmblaTransform(emblaApi);
    rotateWheel(emblaApi);
  }, [emblaApi, inactivateEmblaTransform, rotateWheel, values, selectedValue, onValueChange]);

  // Scroll to selected value when it changes externally
  useEffect(() => {
    scrollToValue(selectedValue);
  }, [selectedValue, scrollToValue]);

  return (
    <div className={`integer-input__ios-picker ${disabled ? 'disabled' : ''}`}>
      <div className="integer-input__ios-picker__scene" ref={rootNodeRef}>
        <div
          className="integer-input__ios-picker__viewport integer-input__ios-picker__viewport--perspective-center"
          ref={emblaRef}
        >
          <div className="integer-input__ios-picker__container">
            {slides.map((index) => (
              <div className="integer-input__ios-picker__slide" key={index}>
                {values[index]}
              </div>
            ))}
          </div>
        </div>
      </div>
      {unit && (
        <div className="integer-input__ios-picker__label">{unit}</div>
      )}
    </div>
  );
};

export default IOSPickerWheel;
